// LanguageCurrencySwitcher - 81 Languages Support
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../context/CurrencyContext';
import { allLanguages, indianLanguages, globalLanguages, Language } from '../../i18n/languages';
import './LanguageCurrencySwitcher.css';

const currencyList = [
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'KRW', symbol: '₩', name: 'Korean Won' },
  { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
  { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
  { code: 'MXN', symbol: 'MX$', name: 'Mexican Peso' },
];

const LanguageCurrencySwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const { currency, setCurrency } = useCurrency();
  const [langOpen, setLangOpen] = useState(false);
  const [currOpen, setCurrOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'indian' | 'global'>('all');
  const langRef = useRef<HTMLDivElement>(null);
  const currRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const currentLang = allLanguages.find(l => l.code === i18n.language) || allLanguages[0];
  const currentCurr = currencyList.find(c => c.code === currency.code) || currencyList[0];

  // Filter languages based on search and tab
  const filteredLanguages = useMemo(() => {
    let languages: Language[] = [];

    switch (activeTab) {
      case 'indian':
        languages = indianLanguages;
        break;
      case 'global':
        languages = globalLanguages;
        break;
      default:
        languages = allLanguages;
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return languages.filter(
        lang =>
          lang.name.toLowerCase().includes(query) ||
          lang.nativeName.toLowerCase().includes(query) ||
          lang.code.toLowerCase().includes(query)
      );
    }

    return languages;
  }, [activeTab, searchQuery]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setLangOpen(false);
        setSearchQuery('');
      }
      if (currRef.current && !currRef.current.contains(event.target as Node)) {
        setCurrOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search when dropdown opens
  useEffect(() => {
    if (langOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [langOpen]);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setSearchQuery('');
  };

  const changeCurrency = (code: string) => {
    setCurrency(code);
    setCurrOpen(false);
  };

  return (
    <div className="lang-curr-switcher d-flex align-items-center gap-3 d-none d-lg-flex">
      {/* Language Dropdown */}
      <div ref={langRef} className="switcher-dropdown">
        <button
          type="button"
          onClick={() => {
            setLangOpen(!langOpen);
            setCurrOpen(false);
          }}
          className="switcher-btn d-flex align-items-center gap-2"
          aria-label="Select language"
        >
          <span className="switcher-flag">{currentLang.flag}</span>
          <span className="switcher-code">{currentLang.code.toUpperCase()}</span>
          <i className={`fas fa-chevron-${langOpen ? 'up' : 'down'} switcher-arrow`}></i>
        </button>

        {langOpen && (
          <div className="switcher-menu lang-menu">
            <div className="lang-menu-header">
              <h5>Select Language</h5>
              <span className="lang-count">81 Languages</span>
            </div>

            {/* Search */}
            <div className="lang-search">
              <i className="fas fa-search"></i>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button
                  className="lang-search-clear"
                  onClick={() => setSearchQuery('')}
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Tabs */}
            <div className="lang-tabs">
              <button
                className={`lang-tab ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All (81)
              </button>
              <button
                className={`lang-tab ${activeTab === 'indian' ? 'active' : ''}`}
                onClick={() => setActiveTab('indian')}
              >
                Indian (22)
              </button>
              <button
                className={`lang-tab ${activeTab === 'global' ? 'active' : ''}`}
                onClick={() => setActiveTab('global')}
              >
                Global (59)
              </button>
            </div>

            {/* Language List */}
            <div className="lang-list">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`lang-item ${lang.code === i18n.language ? 'active' : ''}`}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <div className="lang-info">
                      <span className="lang-native">{lang.nativeName}</span>
                      <span className="lang-name">{lang.name}</span>
                    </div>
                    {lang.code === i18n.language && (
                      <i className="fas fa-check lang-check"></i>
                    )}
                  </button>
                ))
              ) : (
                <div className="lang-empty">
                  <i className="fas fa-search"></i>
                  <p>No languages found</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Currency Dropdown */}
      <div ref={currRef} className="switcher-dropdown">
        <button
          type="button"
          onClick={() => {
            setCurrOpen(!currOpen);
            setLangOpen(false);
          }}
          className="switcher-btn d-flex align-items-center gap-2"
          aria-label="Select currency"
        >
          <span className="switcher-symbol">{currentCurr.symbol}</span>
          <i className={`fas fa-chevron-${currOpen ? 'up' : 'down'} switcher-arrow`}></i>
        </button>

        {currOpen && (
          <div className="switcher-menu curr-menu">
            <div className="curr-menu-header">
              <h5>Select Currency</h5>
            </div>
            <div className="curr-list">
              {currencyList.map(curr => (
                <button
                  key={curr.code}
                  onClick={() => changeCurrency(curr.code)}
                  className={`curr-item ${curr.code === currency.code ? 'active' : ''}`}
                >
                  <span className="curr-symbol">{curr.symbol}</span>
                  <div className="curr-info">
                    <span className="curr-code">{curr.code}</span>
                    <span className="curr-name">{curr.name}</span>
                  </div>
                  {curr.code === currency.code && (
                    <i className="fas fa-check curr-check"></i>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LanguageCurrencySwitcher;
